import React from 'react';
import { FormRenderProps } from '@progress/kendo-react-form';
import { ContentTypeFieldResponse, CpsDocumentMetadata, CPS_CONTENT_TYPE_FIELD_TYPE } from '@cs-cms/types';

export const clearTimer = (fieldName: string, timers: React.RefObject<Record<string, NodeJS.Timeout>>): void => {
  if (timers.current[fieldName]) {
    clearTimeout(timers.current[fieldName]);
    delete timers.current[fieldName];
  }
};

export const setTimer = (
  fieldName: string,
  timer: NodeJS.Timeout,
  timers: React.RefObject<Record<string, NodeJS.Timeout>>
): void => {
  timers.current[fieldName] = timer;
};

export const clearAllTimers = (timers: React.RefObject<Record<string, NodeJS.Timeout>>): void => {
  Object.values(timers.current).forEach(timer => {
    if (timer) {
      clearTimeout(timer);
    }
  });
  timers.current = {};
};

export const buildCurrentFormValues = (
  fieldName: string,
  value: any,
  formRenderProps: FormRenderProps,
  ctFields: ContentTypeFieldResponse[]
): CpsDocumentMetadata => {
  const currentValues: CpsDocumentMetadata = {};

  ctFields.forEach(ctField => {
    currentValues[ctField.name] = formRenderProps.valueGetter(ctField.name);
  });

  currentValues[fieldName] = value;

  return currentValues;
};

export const buildFormValuesWithClearedDependents = (
  fieldName: string,
  value: any,
  formRenderProps: FormRenderProps,
  ctFields: ContentTypeFieldResponse[],
  dependentFields: string[]
): CpsDocumentMetadata => {
  const currentValues: CpsDocumentMetadata = {};

  ctFields.forEach(field => {
    if (dependentFields.includes(field.name)) {
      currentValues[field.name] = undefined;
    } else {
      currentValues[field.name] = formRenderProps.valueGetter(field.name);
    }
  });

  currentValues[fieldName] = value;

  return currentValues;
};

export const isValueEmpty = (value: any): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  return false;
};

export const validateRegex = (value: string, regex: string): boolean => {
  if (!regex) {
    return true;
  }

  const regexPattern = new RegExp(regex);
  return regexPattern.test(value);
};

export const validateMinLength = (value: string, min?: number): boolean => {
  if (min == null) {
    return true;
  }

  return value.length >= min;
};

export const hasReachedExactLength = (value: string, min?: number, max?: number): boolean => {
  if (min == null || max == null || min !== max) {
    return false;
  }

  return value.length === min;
};

export const hasReachedMaxLength = (value: string, max?: number): boolean => {
  if (max == null) {
    return false;
  }

  return value.length >= max;
};

const isFormFillerEligibleField = (field: ContentTypeFieldResponse): boolean =>
  Boolean(field.formFiller && field.usesExtDictionary);

const isChoiceFieldType = (field: ContentTypeFieldResponse): boolean =>
  field.type === CPS_CONTENT_TYPE_FIELD_TYPE.CHOICE ||
  field.type === CPS_CONTENT_TYPE_FIELD_TYPE.MULTI_CHOICE;

const hasFormFillerTriggerConstraints = (field: ContentTypeFieldResponse): boolean => {
  const hasExactLength = field.min != null && field.min === field.max;
  const hasRegex = field.regex != null && field.regex !== '';
  return hasExactLength || hasRegex;
};

const isValueValidForFormFillerTrigger = (field: ContentTypeFieldResponse, stringValue: string): boolean => {
  if (field.regex && !validateRegex(stringValue, field.regex)) {
    return false;
  }

  if (!validateMinLength(stringValue, field.min)) {
    return false;
  }

  return !(field.max != null && stringValue.length > field.max);
};

export const shouldTriggerFormFiller = (field: ContentTypeFieldResponse, value: unknown): boolean => {
  if (!isFormFillerEligibleField(field) || isValueEmpty(value)) {
    return false;
  }

  if (isChoiceFieldType(field)) {
    return true;
  }

  if (!hasFormFillerTriggerConstraints(field)) {
    return false;
  }

  return isValueValidForFormFillerTrigger(field, String(value));
};

export const shouldEndDueToFileDependency = (field: ContentTypeFieldResponse): boolean => {
  return field.usesExtDictionary && field.fileDependent === true;
};

export const isDateComplete = (value: any): boolean => {
  if (!value) {
    return false;
  }

  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }

  if (typeof value === 'string') {
    const date = new Date(value);
    return !isNaN(date.getTime());
  }

  return false;
};

export const fillFormFieldsFromResponse = (
  response: { valuesForOtherFields?: Record<string, any> } | null | undefined,
  formRenderProps: FormRenderProps
): void => {
  if (response?.valuesForOtherFields) {
    Object.entries(response.valuesForOtherFields).forEach(([targetFieldName, targetValue]) => {
      formRenderProps.onChange(targetFieldName, { value: targetValue });
    });
  }
};

export const getDependentFields = (fieldName: string, ctFields: ContentTypeFieldResponse[]): string[] => {
  return ctFields.filter(field => field.dependsOnFields?.includes(fieldName)).map(field => field.name);
};

export const clearDependentFields = (dependentFields: string[], formRenderProps: FormRenderProps): void => {
  dependentFields.forEach(dependentField => {
    formRenderProps.onChange(dependentField, { value: null });
  });
};

export const separateFileMetadata = (
  valuesForOtherFields: Record<string, any>,
  perFileFieldNames: Set<string>
): { perFileMetadata: Record<string, any>; sharedMetadata: Record<string, any> } => {
  const perFileMetadata: Record<string, any> = {};
  const sharedMetadata: Record<string, any> = {};

  Object.entries(valuesForOtherFields).forEach(([key, value]) => {
    if (perFileFieldNames.has(key)) {
      perFileMetadata[key] = value;
    } else {
      sharedMetadata[key] = value;
    }
  });

  return { perFileMetadata, sharedMetadata };
};

export const updateFilesWithPerFileMetadata = (currentFiles: any[], perFileMetadata: Record<string, any>): any[] => {
  return currentFiles.map((fileWithMeta: any) => ({
    ...fileWithMeta,
    perFileMetadata: {
      ...fileWithMeta.perFileMetadata,
      ...perFileMetadata
    }
  }));
};

export const getFileItemKey = (fileItem: { file: { uid?: string; name: string } }): string => {
  return fileItem.file.uid || fileItem.file.name;
};

export const scopedFieldKey = (fileKey: string, fieldName: string): string => `${fileKey}::${fieldName}`;

export const buildPerFileFormValues = (
  formRenderProps: FormRenderProps,
  ctFields: ContentTypeFieldResponse[],
  perFileMetadata: CpsDocumentMetadata,
  changedFieldName?: string,
  changedValue?: CpsDocumentMetadata[string]
): CpsDocumentMetadata => {
  const currentValues: CpsDocumentMetadata = {};

  ctFields.forEach(ctField => {
    if (ctField.fileDependent) {
      currentValues[ctField.name] = perFileMetadata[ctField.name];
    } else {
      currentValues[ctField.name] = formRenderProps.valueGetter(ctField.name);
    }
  });

  if (changedFieldName !== undefined) {
    currentValues[changedFieldName] = changedValue;
  }

  return currentValues;
};

export const buildPerFileFormValuesWithClearedDependents = (
  formRenderProps: FormRenderProps,
  ctFields: ContentTypeFieldResponse[],
  perFileMetadata: CpsDocumentMetadata,
  changedFieldName: string,
  changedValue: CpsDocumentMetadata[string],
  dependentFields: string[]
): CpsDocumentMetadata => {
  const clearedMetadata = { ...perFileMetadata };
  dependentFields.forEach(fieldName => {
    clearedMetadata[fieldName] = undefined;
  });
  clearedMetadata[changedFieldName] = changedValue;

  return buildPerFileFormValues(formRenderProps, ctFields, clearedMetadata);
};

export const clearPerFileDependentMetadata = (
  perFileMetadata: CpsDocumentMetadata,
  dependentFields: string[]
): CpsDocumentMetadata => {
  const cleared = { ...perFileMetadata };
  dependentFields.forEach(fieldName => {
    cleared[fieldName] = undefined;
  });
  return cleared;
};
